export const DEVICE_TYPES = {
    Unknown: 0,
    PersonalComputer: 2,
    ConnectedTV: 3,
    Phone: 4,
    Tablet: 5,
    ConnectedDevice: 6,
    SetTopBox: 7,
} as const;

export type DeviceType = (typeof DEVICE_TYPES)[keyof typeof DEVICE_TYPES];

/** Cable/satellite set-top box signals */
export function isSetTopBoxUserAgent(raw: string): boolean {
    return /arris|pace|technicolor|humax|motorola.*stb|cisco.*stb/i.test(raw);
}

/** Connected TV platform signals */
// Android TV / Google TV UAs omit "mobile" and can otherwise match the tablet branch.
export function isConnectedTVUserAgent(raw: string): boolean {
    return /tizen|web[o0]s|vizio|hbbtv|smart[-\s]?tv|smrttv|tv safari|netcast|android\s*tv|bravia|google\s*tv|opera\s*tv|viera|vidaa|tv\s*build\//i.test(
        raw,
    );
}

/** OTT boxes, dongles, and console signals */
// Before phone/tablet — Fire TV Silk UA contains "Mobile".
export function isConnectedDeviceUserAgent(raw: string): boolean {
    return /roku|\baft[a-z0-9]*\b|amazon.*silk|playstation|xbox|oculus|quest|apple\s*tv|crkey|chromecast|nintendo|wiiu|wii\s*u|steam\s*deck|nexus\s*player|\badt-|mi\s*box|neo[-_x]|doom\s*pro/i.test(
        raw,
    );
}

/**
 * Phone signals. Any remaining Android UA is treated as a phone: set-top boxes, TVs,
 * connected devices, and tablets are matched by higher-precedence rules, so an `android`
 * token reaching this check (with or without a "Mobile" token) indicates a phone.
 */
export function isPhoneDeviceUserAgent(rawUserAgent: string): boolean {
    return /iphone|ipod|windows phone|\bandroid\b/i.test(rawUserAgent);
}

/** Desktop/laptop OS signals */
function isPersonalComputerUserAgent(raw: string): boolean {
    return /windows nt|macintosh|mac os x|linux(?!.*android)|cros/i.test(raw);
}

/**
 * Tablet signals only — generic Android UAs without "mobile" are not treated as tablets.
 * iPadOS 13+ desktop mode relies on maxTouchPoints because the UA mimics Macintosh.
 */
export function isTabletDeviceUserAgent(rawUserAgent: string, maxTouchPoints = 0): boolean {
    const ua = rawUserAgent.toLowerCase();
    const isIPadDesktopMode =
        /macintosh|mac os x/.test(ua) && !/iphone|ipod|ipad/.test(ua) && maxTouchPoints > 1;
    // Samsung Galaxy Tab (SM-T*/SM-P*/SM-X*), generic tablet keyword, Lenovo Tab/TB-* series, GT-P series
    const isAndroidTabletModel =
        /\bsm-[tpx][a-z0-9-]*|\btablet\b|galaxy tab|lenovo tab|\btb-[a-z0-9]|\bgt-p\d/i.test(ua);
    // Kindle Fire tablets use KF* model codes without "mobile" token
    const isKindleFireTablet = /\bkf[a-z0-9]+\b/i.test(ua) && !/mobile/i.test(ua);
    // Non-Amazon Silk UAs without "mobile" (Fire TV is classified earlier via aft/amazon.*silk)
    const isSilkTablet = /silk/i.test(ua) && !/mobile/i.test(ua);

    return (
        isIPadDesktopMode ||
        /ipad|playbook|kindle|nexus[\s_](?:7|9|10)/i.test(ua) ||
        isAndroidTabletModel ||
        isKindleFireTablet ||
        isSilkTablet
    );
}

/**
 * Detects the current device type from the browser's user agent string.
 *
 * Precedence (first match wins): SetTopBox → ConnectedTV → ConnectedDevice →
 * Tablet → Phone → PersonalComputer → Unknown.
 *
 * Returns Unknown when `navigator` is unavailable (e.g. SSR) or the UA is unrecognized.
 */
export function getDeviceType(): DeviceType {
    const raw = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const ua = raw.toLowerCase();
    const maxTouchPoints = typeof navigator !== 'undefined' ? (navigator.maxTouchPoints ?? 0) : 0;

    const rules: { when: boolean; result: DeviceType }[] = [
        { when: isSetTopBoxUserAgent(raw), result: DEVICE_TYPES.SetTopBox },
        { when: isConnectedTVUserAgent(raw), result: DEVICE_TYPES.ConnectedTV },
        { when: isConnectedDeviceUserAgent(raw), result: DEVICE_TYPES.ConnectedDevice },
        { when: isTabletDeviceUserAgent(raw, maxTouchPoints), result: DEVICE_TYPES.Tablet },
        { when: isPhoneDeviceUserAgent(ua), result: DEVICE_TYPES.Phone },
        { when: isPersonalComputerUserAgent(raw), result: DEVICE_TYPES.PersonalComputer },
    ];

    return rules.find(({ when }) => when)?.result ?? DEVICE_TYPES.Unknown;
}
